// roleNarrowings — the ROLE instances of the capability + narrowing-list shape
// (ems-domain PERMISSIONS §Capability + narrowing list), plugged into the shared
// CapabilityEditor's `renderNarrowing` slot and CapabilitySummary's
// `narrowingSummary` slot — the editor stays ignorant of what a role narrows:
//   • users:manage → TWO lists: the managed roles — the family's picker over the project's roles, the chosen as tags —
//     and the responsibility level-set — checkboxes over the fixed enum (the ONE people key carries both, ADR-050)
// A narrowing HELD while its key is off is the dormant state (a setting waiting for its switch — ADR-080 §9): still
// shown, marked Dormant, editable, saved — hidden-but-saved state is how it would get silently destroyed (the
// application credential's device-target kinds, beside its drawer under pages/platform/api-clients, is the same
// case). Strings: the `roles` namespace.

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { PermissionKey } from '@ems-app/schemas/permissions';
import { RESPONSIBILITY_LEVELS as LEVEL_IDS } from '@ems-app/schemas/responsibilities/levels';
import { NarrowingList } from '../NarrowingList.js';
import { Picker } from '../Picker.js';
import { Badge } from '../../ui/Badge.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { rolesSource } from '../../../hooks/useRoles.js';

export interface RoleOption {
  id: number;
  name: string;
}

/** Responsibility levels a role may be allowed to assign — the accountability
 *  level-set narrowing of `users:manage` (the one people key) (ROLE-side, a fixed enum per ADR-026 §E / ADR-049; own-scope is the separate
 *  scope-side bound). Derived from the shared `ResponsibilityLevel` enum (packages/schemas)
 *  — the ONE source for both this role-side level-set and the responsibilities catalogue,
 *  in the canonical hierarchy order. `label` is an i18n key (`roles` namespace). */
export const RESPONSIBILITY_LEVELS: { id: string; label: string }[] = LEVEL_IDS.map((id) => ({
  id,
  label: `level.${id}`,
}));

export function ManagedRolesNarrowing({
  projectId,
  managedRoles,
  onChange,
  granted,
  readOnly = false,
}: {
  projectId: string;
  managedRoles: number[];
  onChange: (ids: number[]) => void;
  /** Whether `users:manage` is granted — off ⇒ the held roles read Dormant. */
  granted: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('roles');
  const source = useMemo(() => rolesSource(projectId), [projectId]);
  const value = useMemo(() => managedRoles.map(String), [managedRoles]);
  return (
    <div className="mt-3">
      <NarrowingList label={t('editor.delegatedRoles')} description={granted ? t('editor.delegatedRolesHelp') : t('editor.dormant', { key: t('permissions:cap.usersManage.label') })}>
        <Picker
          source={source}
          multiple
          selectedDisplay="tags"
          ariaLabel={t('editor.delegatedRoles')}
          placeholder={t('editor.addRole')}
          value={value}
          onChange={(ids) => onChange(ids.map(Number))}
          // the roles wait for their switch: kept and marked while the key is off, the mark lifts when it is granted
          dependsOn={{ value: granted ? 'on' : 'off', case: 'dormant', awaits: 'on' }}
          disabled={readOnly}
        />
      </NarrowingList>
    </div>
  );
}

// Level-set: a fixed small enum, so all options are shown (multi-select checkboxes)
// rather than a picker (which suits long runtime lists like roles).
export function LevelSetNarrowing({
  levels,
  onChange,
  granted,
  readOnly = false,
}: {
  levels: string[];
  onChange: (levels: string[]) => void;
  /** Whether `users:manage` is granted — off ⇒ the held levels are marked Dormant. */
  granted: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('roles');
  return (
    <div className="mt-3">
      <NarrowingList
        label={
          <span className="inline-flex items-center gap-2">
            {t('editor.assignableLevels')}
            {!granted && (
              <Badge variant="neutral" appearance="subtle">
                {t('common:picker.dormant')}
              </Badge>
            )}
          </span>
        }
        description={granted ? t('editor.assignableLevelsHelp') : t('editor.dormant', { key: t('permissions:cap.usersManage.label') })}
      >
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {RESPONSIBILITY_LEVELS.map((lv) => (
            <Checkbox
              key={lv.id}
              checked={levels.includes(lv.id)}
              disabled={readOnly}
              onChange={(on) => onChange(on ? [...levels, lv.id] : levels.filter((x) => x !== lv.id))}
              label={t(lv.label)}
            />
          ))}
        </div>
      </NarrowingList>
    </div>
  );
}

export interface RoleNarrowingProps {
  projectId: string;
  managedRoles: number[];
  onManagedRolesChange: (ids: number[]) => void;
  responsibilityLevels: string[];
  onResponsibilityLevelsChange: (levels: string[]) => void;
  readOnly?: boolean;
}

/** The editor slot — what hangs under a role key: while it is granted, or while it HOLDS a narrowing (dormant). */
export function renderRoleNarrowing(p: RoleNarrowingProps): (key: PermissionKey, granted: boolean) => ReactNode {
  return (key, granted) => {
    if (key !== 'users:manage') return null;
    const roles = granted || p.managedRoles.length > 0;
    const levels = granted || p.responsibilityLevels.length > 0;
    if (!roles && !levels) return null;
    return (
      <>
        {roles && <ManagedRolesNarrowing projectId={p.projectId} managedRoles={p.managedRoles} onChange={p.onManagedRolesChange} granted={granted} readOnly={p.readOnly} />}
        {levels && <LevelSetNarrowing levels={p.responsibilityLevels} onChange={p.onResponsibilityLevelsChange} granted={granted} readOnly={p.readOnly} />}
      </>
    );
  };
}

/** The summary slot — the narrowing detail line for a granted role key. `t` is the
 *  consumer's `roles`-namespace translator. */
export function roleNarrowingSummary(
  t: (key: string) => string,
  s: { managedRoles: number[]; availableRoles: RoleOption[]; responsibilityLevels: string[] },
): (key: PermissionKey) => string | null {
  const roleName = (id: number) => s.availableRoles.find((r) => r.id === id)?.name ?? `#${id}`;
  return (key) => {
    if (key !== 'users:manage') return null;
    const parts: string[] = [];
    if (s.managedRoles.length > 0) parts.push(`${t('editor.delegatedRoles')}: ${s.managedRoles.map(roleName).join(', ')}`);
    if (s.responsibilityLevels.length > 0) parts.push(`${t('editor.assignableLevels')}: ${s.responsibilityLevels.map((id) => t(`level.${id}`)).join(', ')}`);
    return parts.length ? parts.join(' · ') : null;
  };
}

export interface DormantNarrowing {
  label: string;
  values: string;
  /** Why it waits — the key it applies under. */
  note: string;
}

/** The narrowings a role HOLDS while their key is off — the summary is granted-only, so they ride under it, one line
 *  each, marked Dormant (an application credential's dormant kinds row is the same case). `t` is the `roles`
 *  translator. */
export function roleDormantNarrowings(
  t: TFunction,
  s: { grants: readonly string[]; managedRoles: number[]; availableRoles: RoleOption[]; responsibilityLevels: string[] },
): DormantNarrowing[] {
  const roleName = (id: number) => s.availableRoles.find((r) => r.id === id)?.name ?? `#${id}`;
  const out: DormantNarrowing[] = [];
  if (!s.grants.includes('users:manage') && s.managedRoles.length > 0) {
    out.push({ label: t('editor.delegatedRoles'), values: s.managedRoles.map(roleName).join(', '), note: t('editor.dormant', { key: t('permissions:cap.usersManage.label') }) });
  }
  if (!s.grants.includes('users:manage') && s.responsibilityLevels.length > 0) {
    out.push({ label: t('editor.assignableLevels'), values: s.responsibilityLevels.map((id) => t(`level.${id}`)).join(', '), note: t('editor.dormant', { key: t('permissions:cap.usersManage.label') }) });
  }
  return out;
}

/** The dormant narrowings' lines, under a role's capability summary (the review · the detail · the peek). */
export function RoleDormantNarrowings(props: { grants: readonly string[]; managedRoles: number[]; availableRoles: RoleOption[]; responsibilityLevels: string[] }) {
  const { t } = useTranslation('roles');
  const lines = roleDormantNarrowings(t, props);
  if (lines.length === 0) return null;
  return (
    <div className="mt-4 space-y-1 text-sm">
      {lines.map((d) => (
        <p key={d.label}>
          <span className="font-medium">{d.label}</span>: {d.values}
          <span className="text-[var(--color-text-tertiary)]"> — {d.note}</span>
        </p>
      ))}
    </div>
  );
}
